// Krug 5 (K16.2): systems open when they become useful. `/api/me` says which are open and which are
// new (not used yet); the first use tells the server (`POST /api/unlocks/used`), which takes the
// `New` mark away and counts `feature_first_use`. Before /me loads, everything counts as open, so
// nothing flickers away on a slow first load.

import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { MARKED_FEATURES, featureUnlock, scheduleOpensAt, type FeatureId, type MeDTO } from '@lastday/shared';
import { api } from '@/api/client';
import { qk, useMe } from '@/api/hooks';
import { t } from '@/i18n';
import { cx } from '@/lib/format';

/** 10. 10. 2026 (owner): a system's locked text on a schedule: only its level ("Opens at level 4"). Null: not on it. */
export function scheduleLockText(id: FeatureId): string | null {
  const at = scheduleOpensAt(id);
  return at ? t('common.unlocksAtLevel', { n: at.level }) : null;
}

/** The schedule's lock text for this survivor, or null off the schedule (the caller has its own then). */
export function useScheduleLockText(): (id: FeatureId) => string | null {
  const on = useMe().data?.survivor?.scheduleDay != null;
  return useCallback((id: FeatureId) => (on ? scheduleLockText(id) : null), [on]);
}

/** 10. 10. 2026: the level a system opens at for this survivor (a new account's by the schedule, else its own rule). */
export function useOpenLevel(): (id: FeatureId) => number | null {
  const on = useMe().data?.survivor?.scheduleDay != null;
  return useCallback((id: FeatureId) => (on ? (scheduleOpensAt(id)?.level ?? null) : null) ?? featureUnlock(id)?.level ?? null, [on]);
}

/**
 * 10. 10. 2026 (owner): where each system the schedule opens lives: `nav`, the menu entry that has its dot until the
 * player first opens it; `screen`, the screen whose visit counts as opening it (Tobias is reached from Camp).
 */
export const FEATURE_HOME: Partial<Record<FeatureId, { nav: string; screen: string }>> = {
  contracts: { nav: '/camp', screen: '/camp' },
  ads: { nav: '/camp', screen: '/camp' },
  buildings: { nav: '/camp', screen: '/camp' },
  generator: { nav: '/camp', screen: '/camp' },
  radio: { nav: '/camp', screen: '/camp' },
  trader: { nav: '/camp', screen: '/trader' },
  season: { nav: '/season', screen: '/season' },
  craft: { nav: '/workshop', screen: '/workshop' },
  meals: { nav: '/workshop', screen: '/workshop' },
  molotov: { nav: '/workshop', screen: '/workshop' },
  retreat: { nav: '/map', screen: '/prepare' },
  shadow: { nav: '/map', screen: '/prepare' },
  tradeLists: { nav: '/map', screen: '/prepare' },
  truck: { nav: '/map', screen: '/prepare' },
  upgrade: { nav: '/inventory', screen: '/inventory' },
  dismantleBulk: { nav: '/inventory', screen: '/inventory' },
  hordeJoin: { nav: '/horde', screen: '/horde' },
  skills: { nav: '/skills', screen: '/skills' },
  respec: { nav: '/survivor', screen: '/survivor' },
  appearance: { nav: '/survivor/appearance', screen: '/survivor/appearance' },
  collection: { nav: '/collection', screen: '/collection' },
  // 11. 10. 2026 (BACKLOG #33).
  crews: { nav: '/crew', screen: '/crew' },
  // 11. 10. 2026 (owner, docs/thepit.md).
  pit: { nav: '/pit', screen: '/pit' },
  // 11. 10. 2026 (owner): the Market (its own New mark: the screen's first visit takes it).
  market: { nav: '/market', screen: '/market' },
};

/**
 * 10. 10. 2026 (owner): whether a menu entry has a system opened and not used yet (its dot). 14. 10. 2026 (owner): not
 * once its screen was opened (`visitedUnlocks`): Crew at level 8 kept its dot for good, the Survivor's waited for a respec.
 */
export function useNewDot(): (nav: string) => boolean {
  const s = useMe().data?.survivor;
  return useCallback((nav: string) => !!s && s.newUnlocks.some((id) => FEATURE_HOME[id]?.nav === nav && !(s.visitedUnlocks ?? []).includes(id)), [s]);
}

/**
 * 10. 10. 2026 (owner): a visit to a system's screen counts as opening it (its dot goes). Not for a system with its own
 * New mark on that screen (Shadow, the retreat, trade lists, upgrades…): its first use takes the mark and the dot away.
 */
export function useSeenOnVisit() {
  const { pathname } = useLocation();
  const s = useMe().data?.survivor;
  const u = useUnlocks();
  useEffect(() => {
    for (const id of s?.newUnlocks ?? []) {
      const home = FEATURE_HOME[id];
      if (!home || !pathname.startsWith(home.screen)) continue;
      if (!MARKED_FEATURES.includes(id)) u.used(id);
      // 14. 10. 2026 (owner): a system with its own `New` mark loses the menu dot once its screen is opened; the mark
      // stays until the first use.
      else u.visited(id);
    }
  }, [pathname, s?.newUnlocks, u]);
}

/**
 * The dot on a menu entry (a system opened and not used yet). 23. 10. 2026 (owner): `chat`, the same dot on the Crew entry
 * (and the phone's More button) for a crewmate's message not read yet (`lib/crewChat.ts`).
 */
export function NewDot({ show, chat, className }: { show: boolean; chat?: boolean; className?: string }) {
  if (!show && !chat) return null;
  return (
    <span
      data-testid={chat ? 'crew-chat-dot' : 'new-dot'}
      aria-label={chat ? t('clan.chat.unreadDot') : t('common.new')}
      className={cx('size-2 shrink-0 rounded-full bg-text shadow-[0_0_6px_var(--color-text)]', className)}
    />
  );
}

export function useUnlocks() {
  const s = useMe().data?.survivor;
  const qc = useQueryClient();
  const open = useCallback((id: FeatureId) => !s || s.unlocks.includes(id), [s]);
  const isNew = useCallback((id: FeatureId) => !!s && s.newUnlocks.includes(id), [s]);
  const used = useCallback(
    (id: FeatureId) => {
      if (!s?.newUnlocks.includes(id)) return;
      // The mark goes at once; the server keeps it gone.
      qc.setQueryData<MeDTO>(qk.me, (m) => (m?.survivor ? { ...m, survivor: { ...m.survivor, newUnlocks: m.survivor.newUnlocks.filter((x) => x !== id) } } : m));
      void api.post('/unlocks/used', { feature: id }).catch(() => undefined);
    },
    [s, qc],
  );
  // 14. 10. 2026 (owner): its screen opened: the menu dot goes at once; the server keeps it gone.
  const visited = useCallback(
    (id: FeatureId) => {
      if (!s?.newUnlocks.includes(id) || (s.visitedUnlocks ?? []).includes(id)) return;
      qc.setQueryData<MeDTO>(qk.me, (m) => (m?.survivor ? { ...m, survivor: { ...m.survivor, visitedUnlocks: [...(m.survivor.visitedUnlocks ?? []), id] } } : m));
      void api.post('/unlocks/visited', { feature: id }).catch(() => undefined);
    },
    [s, qc],
  );
  return { open, isNew, used, visited };
}

/** The `New` mark on a system just opened, until it is first used. */
export function NewMark({ show, className }: { show: boolean; className?: string }) {
  if (!show) return null;
  return (
    <span data-testid="new-mark" className={cx('inline-flex shrink-0 items-center rounded-[2px] bg-text px-1.5 font-display text-[11px] font-bold uppercase leading-[17px] tracking-[0.1em] text-bg', className)}>
      {t('common.new')}
    </span>
  );
}
